import { supabase } from './supabase.js';

const PARTNER_STATUS_LABELS = {
  pending: 'Pendiente de revisión',
  reviewing: 'En revisión',
  approved: 'Aprobada',
  rejected: 'Rechazada',
  suspended: 'Suspendida',
};

const ROLE_STATUS_LABELS = {
  pending_approval: 'Pendiente de aprobación',
  active: 'Activa',
  rejected: 'Rechazada',
};

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
}

/**
 * Solicitudes enviadas por el usuario a DreamVinyl (partner, roles, comunidad).
 * @param {string} userId
 */
export async function fetchUserSolicitudes(userId) {
  if (!userId) return { items: [], error: null };

  const items = [];

  const { data: partner, error: ePartner } = await supabase
    .from('partners')
    .select('id, company_name, verification_status, rejection_reason, created_at, updated_at')
    .eq('profile_id', userId)
    .maybeSingle();

  if (ePartner) {
    console.error('[userSolicitudesService] partner:', ePartner.message);
    return { items: [], error: ePartner.message };
  }

  if (partner) {
    const st = partner.verification_status || 'pending';
    items.push({
      id: `partner-${partner.id}`,
      kind: 'partner_account',
      title: 'Cuenta partner / negocio',
      subtitle: partner.company_name || 'Solicitud de partner',
      status: st,
      statusLabel: PARTNER_STATUS_LABELS[st] || st,
      detail: st === 'rejected' && partner.rejection_reason ? partner.rejection_reason : null,
      createdAt: partner.created_at,
      updatedAt: partner.updated_at,
      navigatePage: st === 'approved' ? 'partner-panel' : 'registro-tienda',
    });

    const { data: roles, error: eRoles } = await supabase
      .from('partner_roles')
      .select('id, role, role_status, created_at, updated_at')
      .eq('partner_id', partner.id)
      .in('role_status', ['pending_approval', 'rejected'])
      .order('created_at', { ascending: false })
      .limit(20);

    if (!eRoles) {
      for (const r of roles ?? []) {
        const rs = r.role_status || 'pending_approval';
        items.push({
          id: `partner-role-${r.id}`,
          kind: 'partner_role',
          title: `Rol partner: ${r.role}`,
          subtitle: partner.company_name || 'Roles adicionales',
          status: rs,
          statusLabel: ROLE_STATUS_LABELS[rs] || rs,
          detail: null,
          createdAt: r.created_at,
          updatedAt: r.updated_at,
          navigatePage: 'partner-panel',
        });
      }
    }
  }

  const { data: comunidad, error: eCom } = await supabase
    .from('comunidad_publicaciones')
    .select(
      'id, title, created_at, updated_at, aprobada, is_hidden, destacada, destacada_solicitud_pendiente, moderacion_rechazo_codigo, moderacion_rechazo_detalle'
    )
    .eq('author_id', userId)
    .or('destacada_solicitud_pendiente.eq.true,and(aprobada.eq.false,is_hidden.eq.false)')
    .order('created_at', { ascending: false })
    .limit(40);

  if (eCom) {
    console.error('[userSolicitudesService] comunidad:', eCom.message);
  } else {
    for (const p of comunidad ?? []) {
      if (p.destacada_solicitud_pendiente) {
        items.push({
          id: `comunidad-portada-${p.id}`,
          kind: 'comunidad_portada',
          title: p.title || 'Publicación en Comunidad',
          subtitle: 'Solicitud de portada destacada',
          status: 'pending',
          statusLabel: 'Pendiente de revisión',
          detail: null,
          createdAt: p.created_at,
          updatedAt: p.updated_at,
          navigatePage: 'comunidad',
          navigateData: { comunidadId: p.id },
        });
      } else if (!p.aprobada && !p.is_hidden) {
        items.push({
          id: `comunidad-mod-${p.id}`,
          kind: 'comunidad_moderacion',
          title: p.title || 'Publicación en Comunidad',
          subtitle: 'Moderación de publicación',
          status: 'pending',
          statusLabel: 'Pendiente de aprobación',
          detail: null,
          createdAt: p.created_at,
          updatedAt: p.updated_at,
          navigatePage: 'comunidad',
          navigateData: { comunidadId: p.id },
        });
      } else if (p.is_hidden && (p.moderacion_rechazo_codigo || p.moderacion_rechazo_detalle)) {
        items.push({
          id: `comunidad-rech-${p.id}`,
          kind: 'comunidad_moderacion',
          title: p.title || 'Publicación en Comunidad',
          subtitle: 'Publicación no publicada',
          status: 'rejected',
          statusLabel: 'Rechazada por moderación',
          detail: p.moderacion_rechazo_detalle || p.moderacion_rechazo_codigo,
          createdAt: p.created_at,
          updatedAt: p.updated_at,
          navigatePage: 'comunidad',
          navigateData: { comunidadId: p.id },
        });
      }
    }
  }

  items.sort((a, b) => {
    const ta = new Date(a.updatedAt || a.createdAt || 0).getTime();
    const tb = new Date(b.updatedAt || b.createdAt || 0).getTime();
    return tb - ta;
  });

  return { items, error: null };
}

export { fmtDate as solicitudFmtDate };
